import React, { useState, useEffect } from "react";
import { Table, Tag, Select, DatePicker, Input, Modal } from "antd";
import io from "socket.io-client";
import axios from "axios";
import "./InvoiceTable.css";
import PaymentSuccessModal from './PaymentSuccessModal';
import moment from 'moment';


const { Option } = Select;
const { RangePicker } = DatePicker;

const InvoiceTable = ({ invoiceData, onRefresh }) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: invoiceData.length,
  });

  const [updatedInvoiceData, setUpdatedInvoiceData] = useState(invoiceData);
  const [filteredData, setFilteredData] = useState(invoiceData);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [clientId, setClientId] = useState(
    invoiceData.length > 0 ? invoiceData[0].clientId : null
  );

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    dateRange: [], // Replace month with dateRange
    invoiceIds: [],
    paidAmount: ''
    });

  const fetchInvoiceData = async (clientId) => {
    try {
      const response = await axios.get(
        `http://localhost:3339/invoices/api/v1.0/invoicedata?clientId=${clientId}`,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.success && response.data.data) {
        setUpdatedInvoiceData(response.data.data);
        setFilteredData(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.length
        }));
      }
    } catch (error) {
      console.error("Error fetching invoice data:", error);
    }
  };

  const handleInvoiceIdsChange = (value) => {
    setFilters(prev => ({
      ...prev,
      invoiceIds: value ? value.split(',') : []
    }));
  };

  useEffect(() => {
    const socket = io("http://localhost:3339");

    socket.on("invoice-status-updated", async (data) => {
      console.log("Received payment update:", data);

      if (data.transactionId && data.totalPaidAmount !== undefined) {
        setPaymentDetails(data);
        setIsSuccessModalVisible(true);
      }

      if (clientId) {
        await fetchInvoiceData(clientId);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [clientId]);

  useEffect(() => {
    if (invoiceData.length > 0) {
      setClientId(invoiceData[0].clientId);
    }
  }, [invoiceData]);

  // Apply filters
  useEffect(() => {
    let result = [...updatedInvoiceData];

    // Multiple Invoice IDs filter
  if (filters.invoiceIds.length > 0) {
    result = result.filter(invoice =>
      filters.invoiceIds.some(id =>
        invoice.invoiceId.toString().toLowerCase().includes(id.toLowerCase())
      )
    );
  }

    if (filters.status) {
      result = result.filter(invoice => {
        const status = invoice.paymentStatus === 0
          ? "Unpaid"
          : invoice.paymentStatus === 1
          ? "Paid"
          : "Partially Paid";
        return status.toLowerCase() === filters.status.toLowerCase();
      });
    }

   // Date Range filter
   if (filters.dateRange && filters.dateRange.length === 2) {
    const startDate = new Date(filters.dateRange[0]).setHours(0, 0, 0, 0);
    const endDate = new Date(filters.dateRange[1]).setHours(23, 59, 59, 999);
  
    result = result.filter(invoice => {
      const invoiceDate = new Date(invoice.invoiceCreatedDate).getTime();
      return invoiceDate >= startDate && invoiceDate <= endDate;
    });
  }

    setFilteredData(result);
    setPagination(prev => ({
      ...prev,
      total: result.length
    }));
  }, [filters, updatedInvoiceData]);

  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState(null);
  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
  
  const handleInfoClick = (paymentDetails) => {
    setSelectedPaymentDetails(paymentDetails);
    setIsInfoModalVisible(true);
  };
  const getEntryRange = () => {
    const start = (pagination.current - 1) * pagination.pageSize + 1;
    const end = Math.min(pagination.current * pagination.pageSize, filteredData.length);
    const total = filteredData.length;
    return `Showing ${start} - ${end} of ${total} entries`;
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const formatInvoiceDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long' };
    return date.toLocaleDateString("en-US", options);
  };

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const dataSource = filteredData.map((invoice, index) => ({
    key: (pagination.current - 1) * pagination.pageSize + index + 1,
    invoiceId: invoice.invoiceId,
    generatedOn: formatDate(invoice.invoiceCreatedDate),
    invoiceDate: formatInvoiceDate(invoice.invoiceCreatedDate),
    amount: parseFloat(invoice.amount || 0).toFixed(2),
    amountPaid: parseFloat(invoice.paidAmount || 0).toFixed(2),
    paymentDetails: invoice.paymentDetails, // Add this line
    status:
      invoice.paymentStatus === 0
        ? "Unpaid"
        : invoice.paymentStatus === 1
        ? "Paid"
        : "Partially Paid",
    isOverdue: new Date(invoice.dueDate) < new Date(),
  }));

  const columns = [
    {
      title: "Invoice ID",
      dataIndex: "invoiceId",
      key: "invoiceId",
      width: "15%",
      render: (text) => (
        <div style={{ textAlign: "center" }}>
          {text}
        </div>
      ),
    },
    {
      title: "Generated On",
      dataIndex: "generatedOn",
      key: "generatedOn",
      width: "15%",
      render: (text) => (
        <div style={{ textAlign: "center" }}>
          {text}
        </div>
      ),
    },
    {
      title: "Invoice Month",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: "15%",
      render: (text) => (
        <div style={{ textAlign: "center" }}>
          {text}
        </div>
      ),
    },
    {
      title: () => <div style={{ textAlign: "center" }}>Amount</div>,
      dataIndex: "amount",
      key: "amount",
      width: "20%",
      className: "amount-column",
      align: "right",
      render: (text) => (
        <div style={{ textAlign: "right", width: "100%" }}>
          ₹{text}
        </div>
      ),
    },
    {
      title: () => <div style={{ textAlign: "center" }}>Paid Amount</div>,
      dataIndex: "amountPaid",
      key: "amountPaid",
      width: "20%",
      className: "paid-amount-column",
      align: "right",
      render: (text, record) => (
        <div style={{ textAlign: "right", width: "100%", display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <span>₹{text}</span>
          {record.paymentDetails && record.paymentDetails.length > 0 && (
            <button
              className="info-button"
              onClick={(e) => {
                e.stopPropagation();
                handleInfoClick(record.paymentDetails);
              }}
            >
              i
            </button>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "15%",
      render: (status, record) => {
        let color = status === "Paid" ? "green" : status === "Partially Paid" ? "orange" : "red";
        if (record.isOverdue && status === "Unpaid") color = "red";
        return (
          <div style={{ textAlign: "center" }}>
            <Tag color={color}>{status}</Tag>
          </div>
        );
      },
    },
  ];

  const renderPaymentDetailsModal = () => {
    if (!selectedPaymentDetails) return null;
  
    return (
      <Modal
        title={
          <div style={{ color: '#282a77', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
            Payment History
          </div>
        }
        open={isInfoModalVisible}
        onCancel={() => setIsInfoModalVisible(false)}
        footer={null}
        width={600}
      >
        <div className="payment-details-container">
          {selectedPaymentDetails.map((detail, index) => (
            <div key={index} className="payment-detail-item">
              <div className="payment-detail-header">
                Payment {index + 1} of {selectedPaymentDetails.length}
              </div>
              <div className="payment-detail-content">
                <div className="detail-row">
                  <span className="detail-label">Transaction ID:</span>
                  <span className="detail-value">{detail.transactionId}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Amount Paid:</span>
                  <span className="detail-value">₹{parseFloat(detail.amountPaid).toFixed(2)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Payment Date:</span>
                  <span className="detail-value">
                    {moment(detail.paymentDate).format('DD/MM/YYYY HH:mm:ss')}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Previous Balance:</span>
                  <span className="detail-value">₹{parseFloat(detail.previousBalance).toFixed(2)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">New Balance:</span>
                  <span className="detail-value">₹{parseFloat(detail.newBalance).toFixed(2)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    );
  };

  const renderFilterSection = () => (
    <div className="filter-section">
     <div className="filter-group">
      <label>Invoice IDs:</label>
      <Input
        placeholder="Enter Invoice IDs"
        value={filters.invoiceIds.join(',')}
        onChange={(e) => handleInvoiceIdsChange(e.target.value)}
        style={{
          width: '100%',
          minWidth: '200px',
          height: '32px',
        }}
        allowClear
      />
    </div>
      <div className="filter-group">
        <label>Payment Status:</label>
        <Select
          value={filters.status}
          onChange={(value) => handleFilterChange('status', value)}
          style={{ width: 150 }}
        >
          <Option value="">All</Option>
          <Option value="paid">Paid</Option>
          <Option value="unpaid">Unpaid</Option>
          <Option value="partially paid">Partially Paid</Option>
        </Select>
      </div>

      <div className="filter-group">
      <label>Date Range:</label>
      <RangePicker
        value={filters.dateRange}
        onChange={(dates) => handleFilterChange('dateRange', dates)}
        style={{ width: 250 }}
        format="DD/MM/YYYY"
        placeholder={['Start Date', 'End Date']}
        allowClear
      />
    </div>
    </div>
  );

  return (
    <div className="invoice-table-container">
      <div className="table-header-container">
        <h2 className="table-header">Invoice Details</h2>
        <div className="balance-refresh-container">
          <div className="total-due">
            <span className="balance-amount">
              ₹ {filteredData.reduce((sum, invoice) => 
                sum + (parseFloat(invoice.amount || 0) - parseFloat(invoice.paidAmount || 0)), 
                0).toFixed(2)}
            </span>
            <span className="balance-label">Total Due</span>
          </div>
        </div>
      </div>

      {renderFilterSection()}
      {renderPaymentDetailsModal()}

      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
          showQuickJumper: false,
          showPrevNextJumpers: false,
        }}
        onChange={handleTableChange}
      />

      <div className="table-info-container">
        <div className="entries-text">
          {getEntryRange()}
        </div>
      </div>

      <PaymentSuccessModal
        visible={isSuccessModalVisible}
        onClose={() => setIsSuccessModalVisible(false)}
        paymentDetails={paymentDetails}
        formatDateTime={formatDateTime}
      />
    </div>
  );
};

export default InvoiceTable;